import english from "./translations/en.json";
import spanish from "./translations/es.json";
import type { MarketingLanguage } from "./marketingLocales";

// Generated from the same src/i18n catalogs as the app. Only marketing keys and
// this page's language are delivered; the full product dictionaries stay lazy.
export type MarketingTranslations = typeof english;
const catalogs = new Map<MarketingLanguage, MarketingTranslations>([["en", english], ["es", spanish]]);
const failures = new Map<MarketingLanguage, Error>();
const pending = new Map<MarketingLanguage, Promise<void>>();
const loaders = import.meta.glob<{ default: MarketingTranslations }>("./translations/*.json");

if (typeof document !== "undefined") {
  const inlineCatalog = document.querySelector<HTMLScriptElement>("script[data-marketing-translations]");
  if (inlineCatalog?.textContent) {
    const { language, translations } = JSON.parse(inlineCatalog.textContent);
    catalogs.set(language, translations);
  }
}

export function loadMarketingTranslations(language: MarketingLanguage): Promise<void> {
  if (catalogs.has(language)) return Promise.resolve();
  let promise = pending.get(language);
  if (!promise) {
    const loader = loaders[`./translations/${language}.json`];
    if (!loader) return Promise.reject(new Error(`Missing marketing translations: ${language}`));
    promise = loader().then(module => { catalogs.set(language, module.default); }).catch(error => {
      failures.set(language, error instanceof Error ? error : new Error(String(error)));
      throw error;
    });
    pending.set(language, promise);
  }
  return promise;
}

export function getMarketingTranslations(language: MarketingLanguage): MarketingTranslations {
  const catalog = catalogs.get(language);
  if (failures.has(language)) throw failures.get(language);
  if (!catalog) throw loadMarketingTranslations(language);
  return catalog;
}

/** Same named placeholders as the app's catalog; marketing strings need no ICU plurals. */
export function interpolateMarketingTranslation(text: string, values: Record<string, string | number>): string {
  return text.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (placeholder, name) =>
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : placeholder);
}
